import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ErrorAlert from '../../components/common/alerts/ErrorAlert';
import MarketplaceClaimService from '../../services/MarketplaceClaimService';

const passwordValidations = (password, confirmPassword) => {
  const validations = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: password === confirmPassword && password.length > 0
  };

  const isValid = Object.values(validations).every(Boolean);
  return { validations, isValid };
};

const passwordIndicator = (label, condition) => (
  <li key={label} className={`flex items-center space-x-2 ${condition ? 'text-emerald-400' : 'text-slate-500'}`}>
    <span className={`w-2 h-2 rounded-full ${condition ? 'bg-emerald-400' : 'bg-slate-500'}`} />
    <span className="text-xs md:text-sm">{label}</span>
  </li>
);

// Landing tras suscribirse en el listing de AWS Marketplace: AWS redirige al
// comprador al backend (MarketplaceFulfillmentFunction, POST
// /marketplace/registration), que a su vez redirige acá con
// ?registration_id=... (ver FrontendMarketplaceClaimUrl en
// whisperbi-back-security/template.yaml). No hay GET de validación previa
// -- el backend valida el registration_id (existencia/expiración/uso) al
// recibir el POST /marketplace/claim mismo, así que el primer error posible
// se ve recién al enviar el formulario.
const MarketplaceClaim = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('registration_id');

  const [companyName, setCompanyName] = useState('');
  const [employeesCount, setEmployeesCount] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [claimedEmail, setClaimedEmail] = useState('');

  const { validations, isValid: isPasswordValid } = useMemo(
    () => passwordValidations(password, confirmPassword),
    [password, confirmPassword]
  );

  const canSubmit = useMemo(() => {
    if (!registrationId) return false;
    if (!companyName.trim()) return false;
    const parsedEmployees = Number(employeesCount);
    if (!Number.isFinite(parsedEmployees) || parsedEmployees <= 0) return false;
    if (!email.trim()) return false;
    if (!isPasswordValid) return false;
    if (!acceptedTerms) return false;
    return true;
  }, [registrationId, companyName, employeesCount, email, isPasswordValid, acceptedTerms]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      setSubmitError('Completa todos los campos obligatorios antes de continuar.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      await MarketplaceClaimService.claim({
        registrationId,
        email: email.trim(),
        password,
        companyName: companyName.trim(),
        employeesCount: Number(employeesCount),
        acceptedTerms
      });
      setClaimedEmail(email.trim());
    } catch (error) {
      setSubmitError(error?.message || 'No pudimos completar tu alta. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!registrationId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-slate-100 py-10 px-4 flex items-center justify-center">
        <div className="max-w-md w-full rounded-3xl border border-slate-800 bg-slate-950/60 backdrop-blur-lg shadow-2xl p-8 text-center">
          <h1 className="text-2xl font-bold mb-3">Enlace inválido</h1>
          <p className="text-slate-400 mb-6">
            Este enlace no incluye un identificador de registro válido de AWS Marketplace.
            Vuelve a suscribirte desde la página del producto en AWS Marketplace.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 font-semibold transition"
          >
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  if (claimedEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-slate-100 py-10 px-4 flex items-center justify-center">
        <div className="max-w-md w-full rounded-3xl border border-emerald-500/30 bg-emerald-900/20 shadow-2xl p-8 text-center text-emerald-300">
          <h1 className="text-2xl font-bold mb-3">¡Cuenta creada!</h1>
          <p className="text-sm mb-6">
            Tu suscripción de AWS Marketplace ya está vinculada a Whisper BI. Inicia sesión con{' '}
            <strong>{claimedEmail}</strong> y la contraseña que elegiste.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold transition"
          >
            Ir al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-slate-100 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
            Completa tu alta de AWS Marketplace
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Ya confirmamos tu suscripción. Crea tus credenciales de acceso a Whisper BI para
            activar tu plan.
          </p>
        </div>

        <div className="rounded-3xl border border-slate-800 bg-slate-950/60 backdrop-blur-lg shadow-2xl shadow-sky-900/20">
          <section className="grid gap-6 md:grid-cols-2 p-6 md:p-10">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
              <h3 className="text-lg font-semibold mb-2 text-sky-300">Requisitos de la contraseña</h3>
              <ul className="space-y-2">
                {passwordIndicator('Mínimo 8 caracteres', validations.length)}
                {passwordIndicator('Una letra mayúscula', validations.uppercase)}
                {passwordIndicator('Una letra minúscula', validations.lowercase)}
                {passwordIndicator('Un número', validations.number)}
                {passwordIndicator('Un símbolo especial', validations.special)}
                {passwordIndicator('Coincide con la confirmación', validations.match)}
              </ul>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm text-slate-300 mb-2" htmlFor="companyName">
                  Nombre de la empresa
                </label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                  placeholder="Ej. Acme Inc"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2" htmlFor="employeesCount">
                  Número de empleados
                </label>
                <input
                  id="employeesCount"
                  type="number"
                  min="1"
                  value={employeesCount}
                  onChange={(event) => setEmployeesCount(event.target.value)}
                  placeholder="Ej. 50"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2" htmlFor="email">
                  Correo
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="tucorreo@empresa.com"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-slate-300 mb-2" htmlFor="password">
                    Contraseña
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Crea una contraseña segura"
                    className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-2" htmlFor="confirmPassword">
                    Confirmar contraseña
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Repite la contraseña"
                    className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-4 py-3 text-slate-100 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition"
                  />
                </div>
              </div>

              <label className="flex items-start space-x-3 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => setAcceptedTerms(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 focus:ring-sky-500"
                />
                <span>
                  Acepto los términos de uso de Whisper BI y autorizo el tratamiento de mis
                  datos para crear la cuenta.
                </span>
              </label>

              {submitError && <ErrorAlert error={submitError} />}

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full px-8 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creando cuenta...' : 'Crear cuenta y activar plan'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceClaim;
